import type { CmpConfig, TranslationConfig } from "./config";

type ThemeContent = {
  dialogHeading?: string;
  dialogBodyText?: string;
  buttonTexts?: Record<string, string | undefined>;
  compliance?: { defaultLanguage?: string };
};

/**
 * Resolve banner copy: DB translations first, then themeJson.buttonTexts / legacy fields, then defaults.
 */
export function resolveBannerTranslation(config: CmpConfig, lang?: string): TranslationConfig {
  const effectiveLang = lang || config.defaultLanguage || "tr";
  const normalized = effectiveLang.toLowerCase().startsWith("tr") ? "tr" : "en";

  // Language-strict: only use a DB translation whose language matches the user's
  // language. Never fall back to a different-language row — a TR visitor must never
  // be shown EN copy just because a TR row is missing. If there is no matching-language
  // translation, we build localized fallback copy below (in `normalized`).
  const fromDb = config.translations.find(
    (t) => (t.language?.toLowerCase().startsWith("tr") ? "tr" : "en") === normalized
  );

  if (fromDb?.bannerTitle?.trim()) {
    return fromDb;
  }

  const theme = (config.themeJson || {}) as ThemeContent;
  const bt = theme.buttonTexts || {};
  const isTr = normalized === "tr";

  const title =
    fromDb?.bannerTitle ||
    theme.dialogHeading ||
    bt.dialogHeading ||
    (isTr ? "Çerezleri Kullanıyoruz" : "This website uses cookies");

  const description =
    fromDb?.bannerDescription ||
    theme.dialogBodyText ||
    bt.dialogBodyText ||
    (isTr
      ? "Deneyiminizi iyileştirmek için çerezler kullanıyoruz. Tercihlerinizi aşağıdan yönetebilirsiniz."
      : "We use cookies to improve your experience. You can manage your preferences below.");

  const buttonsJson: TranslationConfig["buttonsJson"] = {
    ...(fromDb?.buttonsJson as TranslationConfig["buttonsJson"]),
    rejectAll: bt.declineText || (isTr ? "Tümünü Reddet" : "Reject All"),
    acceptAll: bt.allowAllText || (isTr ? "Tümünü Kabul Et" : "Accept All"),
    managePreferences:
      bt.managePreferences ||
      bt.allowSelectionText ||
      (isTr ? "Çerez Tercihlerimi Yönet" : "Manage Cookie Preferences"),
  };

  return {
    id: fromDb?.id || "resolved",
    language: normalized,
    bannerTitle: title,
    bannerDescription: description,
    buttonsJson,
  };
}
